import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createAgent, isDesktopRuntime } from '../lib/api';
import type { ApiError, CreateAgentResponse } from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { useFocusTrap } from '../hooks/useFocusTrap';

/**
 * Client-side agent-name constraint, mirrored to the server rule: first char
 * lowercase-alphanumeric, then up to 62 more of lowercase-alphanumeric-or-hyphen.
 *
 * The hyphen is ESCAPED (`\-`) so the character class is a valid literal under
 * BOTH the RegExp `u` and `v` flags. Browsers compile the HTML `pattern`
 * attribute with the `v` (unicodeSets) flag, under which a BARE `-` inside a
 * class — leading (`[-…]`), trailing (`[…-]`), or otherwise — is a reserved
 * syntax character and throws `SyntaxError: Invalid character in character
 * class`. That SyntaxError makes the browser discard the whole `pattern`, so
 * client-side validation is silently dropped and every modal open logs an
 * error (validation then only happens server-side, after a 400).
 */
export const AGENT_NAME_PATTERN = '[a-z0-9][a-z0-9\\-]{0,62}';

interface ConnectAgentModalProps {
  onClose: () => void;
  /**
   * When present the modal is pre-targeted at a space: the new agent is
   * enrolled in that space by name (passed as `space` to the create call), so
   * the operator never has to copy a UUID. Omit for the plain
   * Settings → Agents / onboarding flow.
   */
  space?: { id: string; name: string };
}

/** Where the download lives. `GET /download` is a public 302 to the download host
 *  (`web/download_routes.rs`) — the same door the Concierge's honest presence uses. */
const DOWNLOAD_PATH = '/download';

/**
 * Add an agent without copying a UUID — INSTALL FIRST, then connect.
 *
 * Creates a self-mode agent BY NAME via the session-authed `POST /api/agents`
 * (a logged-in web user can mint their own agents — no admin biscuit needed),
 * then hands back a one-click COPY of the `connect_command`
 * — `export TIZOK_TOKEN='…'` — that the operator pastes into their agent's
 * terminal. The raw bearer token is NEVER rendered: the value lives only on
 * the clipboard. When opened from a space, the agent is enrolled in that space
 * atomically (the `space` is part of the create request).
 *
 * ## Why the install step exists (T-0195 / F-ACTIVATION-1)
 *
 * `export TIZOK_TOKEN='…'` is an environment variable. It is not a program. Pasted on a
 * computer that has no `tizok` on its PATH it does nothing at all, silently — and that is
 * exactly what a web-only operator who had just signed up was handed. The #1 dogfood
 * friction: the step between "signed up" and "working" presumed a capability they did not
 * have, and offered no way to get it.
 *
 * Worse than pedagogy: the token is a ONE-TIME secret, shown once. Minting it before the
 * operator can use it meant that leaving to install Tizok cost them the thing they came
 * for. So the sequence is now install → name → command, not name → command → dead end.
 *
 * ## What this modal may not claim
 *
 * **It cannot know whether Tizok is installed.** `isDesktopRuntime()` says we are running
 * INSIDE the desktop app — not that a daemon exists on the machine the agent will run on,
 * which may be a Pi across the room. There is no daemon-presence read from a browser, and
 * `Agent` carries no `connected_at`. So the modal never asserts "you don't have it"; it
 * states the sequence, names what the command is and is not, and puts the door next to it.
 *
 * In the desktop app it opens on the name step — Tizok is demonstrably here — and keeps the
 * install door visible for the case where the agent runs somewhere else.
 */
export function ConnectAgentModal({ onClose, space }: ConnectAgentModalProps) {
  const formId = useId();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  // Running inside the desktop app is proof that Tizok is on THIS machine. It is not proof
  // about the agent's machine, so the install door stays reachable either way.
  const onDesktop = isDesktopRuntime();
  const [step, setStep] = useState<'install' | 'name'>(onDesktop ? 'name' : 'install');

  const [name, setName] = useState('');
  const [created, setCreated] = useState<CreateAgentResponse | null>(null);

  useFocusTrap(modalRef, true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const createMutation = useMutation({
    mutationFn: createAgent,
    onSuccess: (resp) => {
      setCreated(resp);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      // Enrollment happened atomically server-side; refresh the space so its
      // member list shows the new agent.
      if (space) {
        queryClient.invalidateQueries({ queryKey: ['space', space.id] });
      }
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const agentName = name.trim();
    if (!agentName) return;
    createMutation.mutate({ name: agentName, space: space?.id });
  }

  function resetForName(next: string) {
    setName(next);
    setCreated(null);
    createMutation.reset();
  }

  const createErr = createMutation.error as ApiError | null;
  const isNameTaken = createErr?.status === 409;
  const title = space ? `Add an agent to ${space.name}` : 'Connect an agent';

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${formId}-title`}
      >
        <header className="modal-header">
          <h2 id={`${formId}-title`}>{title}</h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        {created ? (
          <ConnectResult
            agentName={name.trim()}
            connectCommand={created.connect_command}
            space={space}
            onClose={onClose}
          />
        ) : step === 'install' ? (
          <InstallStep onNext={() => setStep('name')} onClose={onClose} />
        ) : (
          <form
            className="invite-form"
            aria-label="Connect an agent"
            onSubmit={handleSubmit}
          >
            {onDesktop ? (
              <p className="invite-expires" style={{ opacity: 0.85 }}>
                Tizok is running on this Mac. If this agent runs on{' '}
                <em>another</em> computer, install Tizok there first.{' '}
                <InstallTizokLink inline />
              </p>
            ) : (
              <button
                type="button"
                className="connect-back"
                onClick={() => setStep('install')}
              >
                ← Back to the install step
              </button>
            )}

            <p className="invite-expires" style={{ opacity: 0.85 }}>
              Name the agent and we&apos;ll hand you a one-line command to paste
              on the computer where Tizok is installed. No tokens to copy by hand.
            </p>

            <label className="invite-field" htmlFor={`${formId}-name`}>
              <span>Agent name</span>
              <input
                id={`${formId}-name`}
                type="text"
                value={name}
                onChange={(e) => resetForName(e.target.value)}
                placeholder="e.g. cli-worker"
                pattern={AGENT_NAME_PATTERN}
                disabled={createMutation.isPending}
                required
                autoComplete="off"
              />
            </label>

            {createErr && !isNameTaken && (
              <p className="invite-error" role="alert">
                {createErr.message}
              </p>
            )}

            {isNameTaken && (
              <p className="invite-error" role="alert">
                An agent named <strong>{name.trim()}</strong> already exists.
                Pick another name.
              </p>
            )}

            <div className="invite-actions">
              <button
                type="button"
                className="invite-cancel"
                onClick={onClose}
                disabled={createMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="invite-submit"
                disabled={!name.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Connecting…' : 'Create & connect'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * The door out of the dead end. `/download` is a public 302 to the download host, so it
 * works signed-in or out; `target="_blank"` keeps this modal — and the agent name the
 * operator may already have typed — alive behind it.
 */
function InstallTizokLink({ inline = false }: { inline?: boolean }) {
  return (
    <a
      className={inline ? 'connect-install-inline' : 'connect-install btn-primary'}
      href={DOWNLOAD_PATH}
      target="_blank"
      rel="noopener noreferrer"
    >
      Install Tizok
      <span className="visually-hidden"> (opens in a new tab)</span>
    </a>
  );
}

/**
 * STEP 1. It states the sequence and names what the command is — it never claims to know
 * whether this operator has Tizok, because from a browser nothing can know that.
 */
function InstallStep({ onNext, onClose }: { onNext: () => void; onClose: () => void }) {
  return (
    <div className="invite-form connect-step" aria-label="Install Tizok first">
      <p className="connect-step-lead">
        <strong>An agent runs on a computer you own</strong> — this Mac, a Raspberry Pi, a
        box in the cloud. Tizok has to be installed there before an agent can connect.
      </p>

      <p className="invite-expires">
        The next step hands you a one-line command. It gives Tizok a token to sign in with
        — it is <em>not</em> Tizok itself. On a computer that doesn&apos;t have Tizok, the
        command does nothing.
      </p>

      <p className="connect-install-row">
        <InstallTizokLink />
        <span className="invite-expires">
          Opens the download page in a new tab. This window stays open.
        </span>
      </p>

      <div className="invite-actions">
        <button type="button" className="invite-cancel" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="invite-submit" onClick={onNext}>
          Next: name the agent
        </button>
      </div>
    </div>
  );
}

interface ConnectResultProps {
  agentName: string;
  connectCommand?: string;
  space?: { id: string; name: string };
  onClose: () => void;
}

function ConnectResult({
  agentName,
  connectCommand,
  space,
  onClose,
}: ConnectResultProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );

  // Auto-clear only the success flag. A failure leaves the command revealed
  // (below) so the operator can select it by hand — don't yank it away.
  useEffect(() => {
    if (copyState !== 'copied') return;
    const timer = window.setTimeout(() => setCopyState('idle'), 2000);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  async function handleCopy() {
    if (!connectCommand) return;
    // On failure, reveal the command as selectable text (rendered below) so a
    // one-time secret never becomes an unrecoverable dead-end.
    setCopyState((await copyToClipboard(connectCommand)) ? 'copied' : 'failed');
  }

  const copied = copyState === 'copied';

  return (
    <div className="invite-result">
      <p>
        <strong>{agentName}</strong> is ready to connect.
      </p>

      {connectCommand ? (
        <>
          <button
            type="button"
            className="invite-submit"
            onClick={() => void handleCopy()}
            aria-label="Copy connect command to clipboard"
            style={{ alignSelf: 'flex-start' }}
          >
            {copied ? '✓ Copied connect command' : '📋 Copy connect command'}
          </button>
          <p className="invite-expires">
            Paste this into a terminal on the computer where Tizok is installed. The
            command carries a one-time secret — treat it like a password, and don&apos;t
            paste it in public channels. It won&apos;t be shown again; if you lose it,
            rotate this agent&apos;s token in Settings → Agents.
          </p>
          <p
            className="invite-expires"
            role="status"
            aria-live="polite"
            style={{ minHeight: '1rem' }}
          >
            {copied ? 'Copied to clipboard.' : ''}
          </p>
          {copyState === 'failed' && (
            <div className="invite-field" role="alert">
              <span>
                Copy failed — select the command below and copy it manually.
              </span>
              <input
                type="text"
                value={connectCommand}
                readOnly
                aria-label="Connect command"
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          )}
        </>
      ) : (
        <p className="invite-expires">Agent created.</p>
      )}

      {space && (
        <p className="invite-expires" role="status" aria-live="polite">
          ✓ Added to {space.name}.
        </p>
      )}

      {connectCommand && (
        <p className="invite-expires">
          Tizok not on that computer yet? <InstallTizokLink inline />
        </p>
      )}

      <div className="invite-actions">
        <button type="button" className="invite-submit" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
